import { studentYearSchema } from '@airreading/common/data';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { z } from 'zod';
import { Temporal } from 'temporal-polyfill';

import { InfoPage } from '@/components/info-page';
import { TableErrorState } from '@/components/table/table-error-state';
import { TableFilterInputField } from '@/components/table/table-filter-field';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button';
import { Button } from '@/components/ui/button';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { useOrgOptions, useSchoolOptions } from '@/lib/api-call-hooks';
import { EmptyArray, getAdminSessions } from '@/lib/api-calls';
import { useRowsBulkSelection } from '@/lib/bulk-selection';
import { useErrorToast } from '@/lib/error-handling';
import { paginationRequestSchema } from '@/lib/pagination';
import { usePaginatedFilters } from '@/lib/filtering';
import { CreateSessionModal } from '@/routes/_layout/sessions/-components/create-session-modal';
import { CancelSessions } from '@/routes/_layout/sessions/-components/cancel-sessions';
import { RestoreSessions } from '@/routes/_layout/sessions/-components/restore-sessions';
import { SessionsTable } from '@/routes/_layout/sessions/-components/sessions-table';
import { sessionsTabs } from '@/routes/_layout/sessions/-components/tabs';
import { FrequentTimeZones, isAirAdmin, plainDateFromStringOrElseUndefined } from '@airreading/common';
import { AdminSession } from '@airreading/common/data';
import { TableFilterPlainDateRangePicker } from '@/components/table/table-filter-plain-date-range-picker';
import { BulkRefreshZoomLinks } from '../-components/bulk-refresh-zoom-links';
import { validateRouteRole } from '@/lib/routing';

export const Route = createFileRoute('/_layout/sessions/overlapping/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: OverlappingSessionsIndex,
  validateSearch: paginationRequestSchema.extend({
    query: z.string().optional(),
    orgs: z.string().array().optional(),
    schools: z.string().array().optional(),
    grades: z.array(studentYearSchema).optional(),
    lessons: z.string().array().optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
  }),
});

const today = Temporal.Now.plainDateISO(FrequentTimeZones.UsCentral);
const startOfToday = today.toZonedDateTime(FrequentTimeZones.UsCentral).startOfDay().toInstant();

function OverlappingSessionsIndex() {
  const queryClient = useQueryClient();

  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[query, activeQuery], setQuery],
      orgs: [[selectedOrgs, activeSelectedOrgs], setSelectedOrgs],
      schools: [[selectedSchools, activeSelectedSchools], setSelectedSchools],
      grades: [[selectedGrades, activeSelectedGrades], setSelectedGrades],
      lessons: [[selectedLessons, activeSelectedLessons], setSelectedLessons],
      startDate: [[selectedStartDate], setSelectedStartDate],
      endDate: [[selectedEndDate], setSelectedEndDate],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, ['query', 'orgs', 'schools', 'grades', 'lessons', 'startDate', 'endDate']);

  const endPlanDate = plainDateFromStringOrElseUndefined(selectedEndDate);

  const {
    data: sessionsResp,
    isPending,
    error,
    refetch,
  } = useQuery(
    getAdminSessions({
      pageIndex,
      pageSize,
      filters: {
        startOnOrAfter: selectedStartDate
          ? Temporal.PlainDate.from(selectedStartDate)
              .toZonedDateTime(FrequentTimeZones.UsCentral)
              .startOfDay()
              .toInstant()
              .toString()
          : startOfToday.toString(),
        startOnOrBefore: endPlanDate
          ? endPlanDate
              .toZonedDateTime(FrequentTimeZones.UsCentral)
              .add({ days: 1 })
              .startOfDay()
              .subtract({ milliseconds: 1 })
              .toInstant()
              .toString()
          : undefined,
        hasCoachConflicts: true,
        query: activeQuery,
        orgs: activeSelectedOrgs,
        schools: activeSelectedSchools,
        grades: activeSelectedGrades,
        lessons: activeSelectedLessons,
      },
    }),
  );

  useErrorToast(error, 'Error loading sessions. Please try again.');

  const sessions = useMemo(() => sessionsResp?.adminSessions || EmptyArray, [sessionsResp]);
  const rowCount = sessionsResp?.total || 0;
  const allSchoolOptions = useSchoolOptions();
  const allOrgOptions = useOrgOptions();

  const { rowSelection, rowDataSelection, onRowSelectionChange, getRowId, resetAllRowSelection } =
    useRowsBulkSelection<AdminSession>({
      getRowId: (row) => row.id,
      rowIdToData: (id) => sessions.find((session) => session.id === id),
    });

  const bulkActionsMenuItems = (
    <>
      <CancelSessions
        sessions={rowDataSelection}
        trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Cancel sessions</DropdownMenuItem>}
        onSuccess={resetAllRowSelection}
      />
      <RestoreSessions
        sessions={rowDataSelection}
        trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Restore sessions</DropdownMenuItem>}
        onSuccess={resetAllRowSelection}
      />
      <BulkRefreshZoomLinks
        sessions={rowDataSelection}
        trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Refresh zoom host link</DropdownMenuItem>}
        onSuccess={resetAllRowSelection}
      />
    </>
  );

  const filterLeftComponents = useMemo(
    () => (
      <>
        <TableFilterInputField
          className="w-[250px] lg:w-[350px] xl:w-[400px] 2xl:w-[450px]"
          placeholder="Search sessions by student, teacher, email or lesson name"
          value={query ?? ''}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
        />
        <TableFilterPlainDateRangePicker
          fromPlainDateString={selectedStartDate}
          toPlainDateString={selectedEndDate}
          onRangeSelect={(range) => {
            setSelectedStartDate(range.fromPlainDateString);
            setSelectedEndDate(range.toPlainDateString);
          }}
          placeholder="Date"
        />
        <TableFilterResetButton show onClick={resetFilters} />
      </>
    ),
    [
      query,
      setQuery,
      allOrgOptions,
      selectedOrgs,
      setSelectedOrgs,
      allSchoolOptions,
      selectedSchools,
      setSelectedSchools,
      queryClient,
      selectedGrades,
      setSelectedGrades,
      selectedLessons,
      setSelectedLessons,
      resetFilters,
    ],
  );

  const topBarRightComponents = <CreateSessionModal trigger={<Button>New session</Button>} />;

  return (
    <InfoPage headerText="Sessions" tabs={sessionsTabs('Overlapping sessions')}>
      <SessionsTable
        sessions={sessions}
        hasError={!!error}
        isLoading={isPending}
        errorState={<TableErrorState refetchAction={refetch} />}
        tableHookProps={{
          rowCount,
          state: {
            pagination: {
              pageIndex,
              pageSize,
            },
            rowSelection,
          },
          onPaginationChange,
          onRowSelectionChange,
          getRowId,
        }}
        displayTimeZone={FrequentTimeZones.UsCentral}
        filters={{ filterLeftComponents }}
        topBarRightComponents={topBarRightComponents}
        bulkActionsMenuItems={bulkActionsMenuItems}
        from="overlapping"
      />
    </InfoPage>
  );
}
